import React from 'react'
import PropTypes from 'prop-types'
import { browserHistory } from 'utils/navigation'
import { SortableTable, Button, StickyHeader, SearchInput, Loader } from 'components/UIElements'
import DcfReviewDrawerContainer from '../containers/DcfReviewDrawerContainer'
import AssociateInstanceDrawer from './AssociateInstanceDrawer'
import '../stylesheets/datacorrectionv2.scss'

class DataCorrectionV2Page extends React.Component {
  constructor(props) {
    super(props)
    this.state = { downloadMenuId: null }
    this.headerOptions = [
      { key: 'serialNumber', component: '#' },
      { key: 'actionType', sortable: true, component: 'DCF Action' },
      { key: 'subjectId', sortable: true, component: 'Subject ID' },
      { key: 'instrumentName', sortable: true, component: 'Instrument Name' },
      { key: 'requestedBy', sortable: true, component: 'Requested By' },
      { key: 'createdAt', sortable: true, component: 'Created Date' },
      { key: 'status', sortable: true, component: 'Status' },
      { key: 'action', component: 'Actions' },
    ]
    this.renderFunctions = {
      status: this.renderStatus.bind(this),
      action: this.renderAction.bind(this),
    }
  }

  componentDidMount() {
    const { params } = this.props
    this.loadStudyData(params.studyID)
    document.addEventListener('click', this.onDocumentClick)
  }

  componentDidUpdate(prevProps) {
    const { params, closeDrawer } = this.props
    const prevStudyID = prevProps.params?.studyID
    if (String(params?.studyID) !== String(prevStudyID)) {
      if (closeDrawer) closeDrawer()
      this.loadStudyData(params.studyID)
    }
  }

  componentWillUnmount() {
    const { resetDcfList, closeDrawer } = this.props
    document.removeEventListener('click', this.onDocumentClick)
    if (closeDrawer) closeDrawer()
    resetDcfList()
  }

  onDocumentClick = () => {
    if (this.state.downloadMenuId != null) {
      this.setState({ downloadMenuId: null })
    }
  }

  loadStudyData = studyID => {
    const { resetDcfList, fetchDcfList, fetchDcfLookups, fetchDcfStatuses, fetchDcfActions } =
      this.props
    resetDcfList()
    fetchDcfStatuses()
    fetchDcfActions()
    fetchDcfLookups(studyID)
    fetchDcfList(studyID)
  }

  onCreateDcf = () => {
    const { params } = this.props
    const { studyID, studyVersionID } = params
    browserHistory.push(`/studies/${studyID}/${studyVersionID}/data-correction-v2/create`)
  }

  toggleDownloadMenu = (e, dcfId) => {
    e.stopPropagation()
    this.setState(prev => ({
      downloadMenuId: prev.downloadMenuId === dcfId ? null : dcfId,
    }))
  }

  onDownloadPdf = (e, dcfId) => {
    e.stopPropagation()
    this.setState({ downloadMenuId: null })
    const { downloadDcf, params } = this.props
    downloadDcf(params.studyID, dcfId)
  }

  onDownloadCsv = (e, dcfId) => {
    e.stopPropagation()
    this.setState({ downloadMenuId: null })
    const { downloadDcfCsv, params } = this.props
    downloadDcfCsv(params.studyID, dcfId)
  }

  onAssociate = (e, row) => {
    e.stopPropagation()
    const {
      params,
      openDrawer,
      closeDrawer,
      searchDcfInstances,
      associateDcfInstance,
      fetchDcfList,
      fetchDcfDetail,
    } = this.props

    const openWithDetail = detail => {
      const participantId = detail?.participant_id ?? row.participantId
      const instrumentId = detail?.instrument_id ?? row.instrumentId
      const instanceId = detail?.instance_id ?? row.instanceId ?? null
      const subjectLabel =
        detail?.subject_id || detail?.participant_subject_id || row.subjectId || participantId
      const instrumentLabel =
        detail?.instrument_name || detail?.instrument_title || row.instrumentName || instrumentId

      openDrawer({
        closeOnBackgroundClick: true,
        content: (
          <AssociateInstanceDrawer
            studyID={params.studyID}
            dcfId={row.id}
            participantId={participantId}
            instrumentId={instrumentId}
            currentInstanceId={instanceId || null}
            dcfAction={detail?.action || row.dcfAction || null}
            subjectLabel={subjectLabel}
            instrumentLabel={instrumentLabel}
            searchDcfInstances={searchDcfInstances}
            associateDcfInstance={associateDcfInstance}
            fetchDcfList={fetchDcfList}
            closeDrawer={closeDrawer}
          />
        ),
        heading: 'Associate instance',
        onCancel: () => closeDrawer(),
      })
    }

    // Prefer fresh detail so participant/instrument/version are never stale/missing.
    if (fetchDcfDetail) {
      fetchDcfDetail(params.studyID, row.id).then(detail => openWithDetail(detail || {}))
      return
    }
    openWithDetail({})
  }

  onReview = (e, dcfId) => {
    e.stopPropagation()
    const { fetchDcfDetail, openDrawer, closeDrawer, params, setDcfDetail } = this.props
    setDcfDetail(null)
    fetchDcfDetail(params.studyID, dcfId).then(detail => {
      if (!detail?.id || String(detail.id) !== String(dcfId)) return
      openDrawer({
        closeOnBackgroundClick: true,
        content: (
          <DcfReviewDrawerContainer
            dcfDetail={detail}
            studyID={params.studyID}
          />
        ),
        heading: 'DCF Review',
        onCancel: () => closeDrawer(),
      })
    })
  }

  renderStatus = rowProps => {
    const status = (rowProps.value || '').toString()
    const statusClass = status.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-')
    return (
      <td key='status-cell' className='status-cell'>
        <span className={`dcf-status ${statusClass}`}>{status}</span>
      </td>
    )
  }

  renderAction = row => {
    const {
      id,
      canDownload,
      canDownloadCsv,
      canAssociate,
      participantId,
      instrumentId,
      instanceId,
      subjectId,
      instrumentName,
      dcfAction,
    } = row
    const { downloadMenuId } = this.state
    const showDownload = canDownload !== false || canDownloadCsv

    return (
      <td className='action-cell' key={`action_${id}`}>
        <div className='flexed start-justified action-buttons'>
          {showDownload && (
            <div className='dcf-download-menu'>
              <Button
                link
                type='button'
                id={`download-dcf-${id}`}
                icon='fas fa-cloud-download-alt'
                aria-label='Download DCF'
                aria-haspopup='true'
                aria-expanded={downloadMenuId === id}
                title='Download'
                onClick={e => this.toggleDownloadMenu(e, id)}
              />
              {downloadMenuId === id && (
                <ul
                  className='dcf-download-submenu'
                  role='menu'
                  onClick={e => e.stopPropagation()}
                >
                  {canDownload !== false && (
                    <li role='none'>
                      <button
                        type='button'
                        role='menuitem'
                        onClick={e => this.onDownloadPdf(e, id)}
                      >
                        Download PDF
                      </button>
                    </li>
                  )}
                  {canDownloadCsv && (
                    <li role='none'>
                      <button
                        type='button'
                        role='menuitem'
                        onClick={e => this.onDownloadCsv(e, id)}
                      >
                        Download CSV
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}
          <Button
            link
            type='button'
            id={`review-dcf-${id}`}
            icon='fas fa-eye'
            aria-label='Review DCF'
            title='Review'
            onClick={e => this.onReview(e, id)}
          />
          {canAssociate && (
            <Button
              link
              type='button'
              id={`associate-dcf-${id}`}
              icon='fas fa-link'
              aria-label='Associate instance'
              title={instanceId ? 'Replace associated instance' : 'Associate instance'}
              onClick={e =>
                this.onAssociate(e, {
                  id,
                  participantId,
                  instrumentId,
                  instanceId,
                  subjectId,
                  instrumentName,
                  dcfAction,
                })
              }
            />
          )}
        </div>
      </td>
    )
  }

  render() {
    const { dcfList = [], studyLock, searchTerm, loading } = this.props

    return (
      <div className={`data-correction-v2 page${loading ? ' is-loading' : ''}`}>
        {loading && (
          <div className='dcf-page-loader-overlay' aria-busy='true' aria-live='polite'>
            <Loader />
          </div>
        )}
        <StickyHeader offset={0}>
          <div />
          <div className='header-button-list flexed end-justified'>
            <Button
              id='new-dcf'
              content='New DCF'
              disabled={studyLock || loading}
              onClick={this.onCreateDcf}
            />
          </div>
        </StickyHeader>
        <StickyHeader offset={56} className='search'>
          <div />
          <SearchInput />
        </StickyHeader>
        <SortableTable
          className='dcf-list'
          headerOptions={this.headerOptions}
          renderFunctions={this.renderFunctions}
          rowList={dcfList}
          sortingBy='status'
          initialOrder='ascending'
          searchTerm={searchTerm}
          searchKeys={['subjectId', 'instrumentName', 'actionType', 'requestedBy', 'createdAt', 'status']}
          emptyText='No Data Correction Requests'
        />
      </div>
    )
  }
}

DataCorrectionV2Page.propTypes = {
  params: PropTypes.shape({
    studyID: PropTypes.string,
    studyVersionID: PropTypes.string,
  }),
  dcfList: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
      }),
    ),
  ),
  loading: PropTypes.bool,
  studyLock: PropTypes.bool,
  searchTerm: PropTypes.string,
  fetchDcfList: PropTypes.func,
  fetchDcfLookups: PropTypes.func,
  fetchDcfStatuses: PropTypes.func,
  fetchDcfActions: PropTypes.func,
  resetDcfList: PropTypes.func,
  downloadDcf: PropTypes.func,
  downloadDcfCsv: PropTypes.func,
  searchDcfInstances: PropTypes.func,
  associateDcfInstance: PropTypes.func,
  fetchDcfDetail: PropTypes.func,
  setDcfDetail: PropTypes.func,
  openDrawer: PropTypes.func,
  closeDrawer: PropTypes.func,
}

export default DataCorrectionV2Page
